import Brightness1Icon from "@mui/icons-material/Brightness1";
import Box from "@mui/material/Box";
import MuiRadio, { RadioProps as MuiRadioProps } from "@mui/material/Radio";
import { styled } from "@mui/material/styles";
import React, { FunctionComponent } from "react";
import { blue, grey, red } from "designSystem/styles/color";

const OuterBox = styled(Box)(({ theme }) => ({
	backgroundColor: "#FFFFFF",
	borderColor: grey[600],
	borderRadius: "8px",
	borderStyle: "solid",
	borderWidth: "1px",
	display: "inline-flex",
	height: "16px",
	width: "16px",

	// necessary to override default of baseline which causes extra vertical padding
	// reference: https://stackoverflow.com/questions/48117071/element-with-display-inline-flex-has-a-strange-top-margin
	verticalAlign: "middle",

	"&:hover": {
		borderColor: grey[900],
	},
	".csi-outer-radio-label:hover &": {
		borderColor: grey[900],
	},
	"&:active": {
		borderColor: theme.palette.PrimaryDefault,
	},
	"&:focus-within": {
		borderColor: theme.palette.PrimaryDefault,
		boxShadow: `0 0 0px 2px ${theme.palette.FocusPrimary}`,
	},

	"&.csi-radio-checked-outer-box": {
		borderColor: blue[600],

		"&:active": {
			borderColor: blue[600],
		},
		"&:hover": {
			borderColor: blue[600],
		},
		".csi-outer-radio-label:hover &": {
			borderColor: blue[600],
		},

		"&:focus-within": {
			borderColor: blue[600],
		},
	},
}));

const DisabledOuterBox = styled(OuterBox)(({ theme }) => ({
	backgroundColor: grey[100],
	borderColor: grey[700],
	cursor: "not-allowed",

	opacity: 0.65,

	"&:hover": {
		borderColor: grey[700],
	},
	".csi-outer-radio-label:hover &": {
		borderColor: grey[700],
	},
}));

const InvalidOuterBox = styled(OuterBox)(({ theme }) => ({
	borderColor: red[600],

	"&:active": {
		borderColor: red[600],
	},
	"&:hover": {
		borderColor: red[600],
	},
	".csi-outer-radio-label:hover &": {
		borderColor: red[600],
	},

	"&:focus-within": {
		borderColor: red[600],
	},
}));

const StyledCheck = styled(Brightness1Icon)(({ theme }) => ({
	color: theme.palette.PrimaryDefault,
	fontSize: "14px",
	height: "12px",
	width: "12px",
}));

const DisabledCheck = styled(StyledCheck)(({ theme }) => ({
	color: grey[700],
}));

const InvalidCheck = styled(StyledCheck)(({ theme }) => ({
	color: red[600],
}));

const StyledRadio = styled(MuiRadio)(({ theme }) => ({
	height: "100%",
	padding: 0,
	width: "100%",
}));

export interface RadioProps extends Omit<MuiRadioProps, "size"> {
	invalid?: boolean;
}

const Radio: FunctionComponent<RadioProps> = (props) => {
	const { invalid, ...otherProps } = props;

	let OuterComponent = OuterBox;
	let CheckComponent = StyledCheck;

	let outerBoxClass;

	if (props.invalid) {
		CheckComponent = InvalidCheck;
		OuterComponent = InvalidOuterBox;
	} else if (props.disabled) {
		CheckComponent = DisabledCheck;
		OuterComponent = DisabledOuterBox;
	} else if (props.checked) {
		outerBoxClass = "csi-radio-checked-outer-box";
	}

	return (
		<OuterComponent className={outerBoxClass}>
			<StyledRadio
				checkedIcon={<CheckComponent />}
				data-csi-automation="RadioButton"
				icon={<Box sx={{ fontSize: "14px" }} />}
				size="small"
				{...otherProps}
			/>
		</OuterComponent>
	);
};

export default Radio;